import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { links } from '../../constants/links';
import { useClipboard } from '../../hooks/useClipboard';
import { toast } from 'react-hot-toast';
import { Check, Copy, Gamepad2, X } from 'lucide-react';
import { useServerStatus } from '../../hooks/useServerStatus';
import { motion, useAnimation, useInView, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';

export function Hero() {
  const { t } = useTranslation();
  const { copyToClipboard } = useClipboard();
  const serverStatus = useServerStatus(links.serverIp);
  const discordLogo = '/images/discord-mark-white.svg';
  const prefersReducedMotion = useReducedMotion();

  const handlePlayNow = async () => {
    const copied = await copyToClipboard(links.serverIp);
    if (copied) {
      toast.success(
        <div className="flex items-center whitespace-nowrap">
          <span className="font-medium tracking-wide">{links.serverIp}</span>
          <span className="text-gray-300 text-sm ml-3">{t('navbar.copied')}</span>
        </div>
      );
    } else {
      toast.error(
        <div className="flex items-center whitespace-nowrap">
          <span className="text-gray-300 text-sm">{t('navbar.copyError')}</span>
        </div>
      );
    }
  };

  // Staggered animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 100
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        delay: 0.1
      }
    }
  };

  const gradientTextVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        delay: 0.3
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 200,
        delay: 0.6
      }
    },
    hover: {
      scale: 1.05,
      transition: {
        type: "spring",
        damping: 10,
        stiffness: 300
      }
    },
    tap: {
      scale: 0.98
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with premium gradient overlay and subtle animation */}
      <motion.div 
        className="absolute inset-0 hero-pattern bg-cover bg-center"
        initial={{ scale: 1.1, opacity: 0.8 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      >
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/40 to-gray-900/100"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
        />
      </motion.div>

      {/* Floating particles effect */}
      <AnimatePresence>
        {!prefersReducedMotion && Array.from({ length: 8 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-primary-400/30"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              scale: Math.random() * 0.5 + 0.5,
              opacity: 0
            }}
            animate={{
              y: [null, Math.random() * -100 - 50],
              opacity: [0, 0.7, 0],
              scale: [null, Math.random() * 1 + 1]
            }}
            transition={{
              duration: Math.random() * 10 + 15,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut"
            }}
          />
        ))}
      </AnimatePresence>

      {/* Content */}
      <motion.div 
        className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="space-y-8">
          {/* Main heading with animated gradient text */}
          <motion.div variants={itemVariants}>
            <motion.h1 
              className="text-5xl md:text-7xl font-extrabold tracking-tight"
              variants={titleVariants}
            >
              {t('hero.welcome')}
              <motion.div 
                className="mt-2 bg-gradient-to-r from-primary-400 to-yellow-300 text-transparent bg-clip-text animate-gradient"
                variants={gradientTextVariants}
              >
                Kaizen Network
              </motion.div>
            </motion.h1>
          </motion.div>
          
          {/* Tagline with improved typography */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
            {t('hero.tagline')}
          </motion.p>

          {/* Server status indicator */}
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-center mb-2"
          >
            <motion.div 
              className="flex items-center bg-gray-800/70 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-700"
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <motion.div 
                className={`w-2.5 h-2.5 rounded-full mr-2 ${serverStatus.online ? 'bg-green-500' : 'bg-red-500'}`}
                animate={serverStatus.online ? { 
                  scale: [1, 1.2, 1],
                  opacity: [1, 0.7, 1]
                } : {}}
                transition={serverStatus.online ? { 
                  repeat: Infinity,
                  duration: 1.5
                } : {}}
              />
              <span className="text-sm font-medium text-gray-300">
                {serverStatus.online 
                  ? `${t('hero.serverStatus')} · ${serverStatus.players.online} Players · ${t('hero.playNowStatus')}` 
                  : t('stats.offline')}
              </span>
            </motion.div>
          </motion.div>

          {/* Call to action buttons with hover effects */}
          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-6"
            variants={itemVariants}
          >
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button 
                variant="primary" 
                onClick={handlePlayNow} 
                className="group relative px-8 py-3 text-lg font-medium shadow-[0_0_0_rgba(248,203,8,0)] transition-[background,box-shadow] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] hover:shadow-[0_8px_16px_rgba(248,203,8,0.15)] hover:bg-yellow-400/90"
              >
                <div className="flex items-center space-x-3">
                  <Gamepad2 className="w-6 h-6 transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-105" />
                  <span>{t('hero.playNow')}</span>
                </div>
              </Button>
            </motion.div>

            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button 
                variant="outline" 
                as="a" 
                href={links.discord}
                className="group relative px-8 py-3 text-lg font-medium border shadow-[0_0_0_rgba(255,255,255,0)] transition-[background,border-color,box-shadow] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] hover:shadow-[0_8px_16px_rgba(255,255,255,0.08)] hover:border-white/20 hover:bg-white/[0.03]"
              >
                <div className="flex items-center space-x-3">
                  <img src={discordLogo} alt="Discord" className="w-6 h-6 transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-105" />
                  <span>{t('hero.joinDiscord')}</span>
                </div>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}